import { COUNTRY_CODES, ValidationForm, ValidationSummary } from '@ygtechnologies/bygr-frontend-component-library';
import theme from './EditProfileModal.module.scss';
import React, { useState } from 'react';
import { addProfileOptions, setProfileSettings } from '@/services/profiles';
import { useAsyncState } from '@/utils/hooks';
import { getCurrencies } from '@/services/currencies';
import { BygrProfileDetails } from '@/types';
import { Button, ButtonBar, Dialog } from '@ygtechnologies/rubus-ui';

interface EditProfileModalProps {
    isOpen: boolean;
    profile: BygrProfileDetails;
    toggle(): void;
    onSave(): void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, profile, toggle, onSave }) => {
    const [profileOptions, setProfileOptions] = useState<
        Pick<BygrProfileDetails, 'countryCode' | 'defaultUnitOfMeasurement'> & { currencyId: number }
    >({
        currencyId: profile.currency?.id ?? 1,
        countryCode: profile.countryCode,
        defaultUnitOfMeasurement: profile.defaultUnitOfMeasurement,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [, setSkipNameValidation] = useState(false);
    const [currencies] = useAsyncState(() => getCurrencies(), [], null);

    const handleAddProfile = async () => {
        setIsSaving(true);
        try {
            await setProfileSettings(profile.id, {
                currencyId: profileOptions.currencyId,
                countryCode: profileOptions.countryCode,
            });
            onSave();
            toggle();
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (changes: Partial<addProfileOptions>, resetNameCheck: boolean = false) => {
        if (resetNameCheck) {
            setSkipNameValidation(false);
        }

        setProfileOptions((prevValue) => ({ ...prevValue, ...changes }));
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && toggle()}>
            <Dialog.Content contentWidth="sm">
                <Dialog.Header>Edit profile settings for {profile.name}</Dialog.Header>
                <ValidationForm onSubmit={handleAddProfile}>
                    <div className={theme.body}>
                        <div className="form columns-1">
                            <label>
                                Country
                                <select
                                    className="form-control"
                                    value={profileOptions.countryCode}
                                    onChange={(e) =>
                                        handleChange({ countryCode: e.target.value as keyof typeof COUNTRY_CODES })
                                    }>
                                    {Object.entries(COUNTRY_CODES).map(([key, value]) => (
                                        <option key={key} value={key}>
                                            {value}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {currencies && (
                                <label>
                                    Currency
                                    <select
                                        className="form-control"
                                        value={profileOptions.currencyId}
                                        onChange={(e) => handleChange({ currencyId: +e.target.value })}>
                                        {currencies.map((x) => (
                                            <option key={x.id} value={x.id}>
                                                {x.name} ({x.code})
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                        </div>
                        <ValidationSummary />
                    </div>
                    <ButtonBar>
                        <ButtonBar.Right>
                            <Button onClick={toggle} variant="tertiary">
                                Cancel
                            </Button>
                            <Button loading={isSaving} type="submit" variant="primary">
                                Update settings
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ValidationForm>
            </Dialog.Content>
        </Dialog>
    );
};

export default EditProfileModal;
